import { useCallback, useEffect, useState } from "react";

export const FLOATING_ANI_PROFILE_COMPANY_STORAGE_KEY = "ani.floatingAni.profileCompanyId";

const SYNC_EVENT = "ani:floating-profile-company-changed";

function readStoredProfileCompanyId(): string {
  try {
    return localStorage.getItem(FLOATING_ANI_PROFILE_COMPANY_STORAGE_KEY) ?? "";
  } catch {
    return "";
  }
}

function writeStoredProfileCompanyId(value: string): void {
  try {
    const trimmed = value.trim();
    if (!trimmed) {
      localStorage.removeItem(FLOATING_ANI_PROFILE_COMPANY_STORAGE_KEY);
    } else {
      localStorage.setItem(FLOATING_ANI_PROFILE_COMPANY_STORAGE_KEY, trimmed);
    }
  } catch {
    // ignore quota / private mode
  }
}

/** Staff-only persisted tenant override for Floating Ani profile resolution. */
export function useFloatingAniProfileCompanyId(enabled: boolean): {
  profileCompanyId: string;
  setProfileCompanyId: (next: string) => void;
} {
  const [profileCompanyId, setProfileCompanyIdState] = useState(() =>
    enabled ? readStoredProfileCompanyId() : "",
  );

  useEffect(() => {
    if (!enabled) {
      setProfileCompanyIdState("");
      return;
    }
    const sync = () => setProfileCompanyIdState(readStoredProfileCompanyId());
    window.addEventListener(SYNC_EVENT, sync);
    return () => window.removeEventListener(SYNC_EVENT, sync);
  }, [enabled]);

  const setProfileCompanyId = useCallback(
    (next: string) => {
      if (!enabled) return;
      writeStoredProfileCompanyId(next);
      setProfileCompanyIdState(next.trim());
      window.dispatchEvent(new Event(SYNC_EVENT));
    },
    [enabled],
  );

  return { profileCompanyId, setProfileCompanyId };
}
